import { useTranslation } from "react-i18next";
import { SlidersHorizontal } from "lucide-react";
import { Dialog } from "react-aria-components";
import { Button } from "@/components/ui/button";
import { SearchField } from "@/components/ui/search-field";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { ManualFiltersPanel } from "./ManualFiltersPanel.js";
import { manualFiltersActive, type ManualFilters } from "./manual-filters.js";

/**
 * The manuals-view toolbar controls (mockup #14/#15): a name **search** and the (deferred) advanced-**filter** button.
 * Shared so the drill-down header ({@link ManualsViewShell}, where they sit on the title row) and the classic pane render
 * the SAME controls. Search is controlled.
 */
export function ManualsToolbarControls({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  creators,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  /** Advanced filters + setter + the facility's creators — when provided, the sliders button opens the filter POPOVER
   *  (a dot shows when any filter is set); absent ⇒ a disabled "coming soon" placeholder (the classic pane). */
  filters?: ManualFilters;
  onFiltersChange?: (f: ManualFilters) => void;
  creators?: string[];
}) {
  const { t } = useTranslation();
  return (
    <div className="flex w-full min-w-0 items-center gap-2 sm:w-80">
      <SearchField
        label={t("manualManagement.toolbar.search")}
        placeholder={t("manualManagement.toolbar.search")}
        value={search}
        onChange={onSearchChange}
        clearLabel={t("manualManagement.toolbar.clearSearch")}
        className="min-w-0 flex-1"
      />
      {/* More filters (mockup #16) — a POPOVER anchored to this button; a dot shows when any is set. Disabled
          placeholder only where no handler is wired (the classic pane). */}
      {filters && onFiltersChange ? (
        <PopoverTrigger>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t(manualFiltersActive(filters) ? "manualManagement.filters.moreActive" : "manualManagement.filters.more")}
            className="relative"
          >
            <SlidersHorizontal aria-hidden="true" className="size-4" />
            {manualFiltersActive(filters) ? (
              <span aria-hidden="true" className="bg-primary absolute -top-0.5 -right-0.5 size-2 rounded-full" />
            ) : null}
          </Button>
          <Popover className="w-80" placement="bottom end">
            <Dialog aria-label={t("manualManagement.filters.more")} className="outline-none">
              {({ close }) => (
                <ManualFiltersPanel filters={filters} onChange={onFiltersChange} creators={creators ?? []} onClose={close} />
              )}
            </Dialog>
          </Popover>
        </PopoverTrigger>
      ) : (
        <TooltipTrigger delay={300} excludeFromTabOrder>
          <span className="inline-flex">
            <Button variant="outline" size="icon-sm" isDisabled aria-label={t("manualManagement.filters.comingSoon")}>
              <SlidersHorizontal aria-hidden="true" className="size-4" />
            </Button>
          </span>
          <Tooltip>{t("manualManagement.filters.comingSoonTip")}</Tooltip>
        </TooltipTrigger>
      )}
    </div>
  );
}
